import { TopMenuItem } from '@/components/topmenuitem';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import C from '@shared/constants/general';
import { getImage, ImageSize } from '@shared/utils/imageFormat';
import VisitorNav from '@/js/shared/components/VisitorNav';
import { AuthContext, ConferenceContext, MenuContext } from '@/store';
import { findCurrentDayId } from '@/lib/utils/DataFormat';
import { useLinkAction } from '@/hooks/useLinkAction';

import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

const drawerWidth = 180;

const useStyles = (theme) => ({
  root: {
    display: 'flex !important',
  },
  title: {
    flexGrow: '1 !important',
  },
  appBarStyle: {
    height: '64px !important',
    background: 'transparent !important',
  },
  appBar: {
    transition: `${theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    })} !important`,
  },
  menuButton: {
    marginRight: `${theme.spacing(2)} !important`,
  },
  hide: {
    display: 'none !important',
  },
  drawer: {
    fontSize: '0.8rem !important',
    backgroundColor: `${theme.palette.background.shadeup_10} !important`,
    zIndex: '1200 !important',
    position: 'fixed !important',
    top: '0 !important',
    left: '0 !important',
    bottom: '0 !important',
    width: `${drawerWidth}px !important`,
    flexShrink: '0 !important',
    marginLeft: `-${drawerWidth}px !important`,
  },
  drawerPaper: {
    width: `${drawerWidth}px !important`,
  },
  drawerHeaderIcon: {
    color: `${theme.palette.text.primary} !important`,
  },
  drawerHeader: {
    display: 'flex !important',
    alignItems: 'center !important',
    padding: `${theme.spacing(0, 1)} !important`,
    minHeight: '48px !important',
    justifyContent: 'flex-end !important',
  },
  content: {
    height: 'calc(100vh - 48px) !important',
    display: 'flex !important',
    padding: `${theme.spacing(0)} !important`,
    transition: `${theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    })} !important`,
    marginLeft: '0 !important',
  },
  contentShift: {
    transition: `${theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    })} !important`,
    marginLeft: `${drawerWidth}px !important`,
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0 !important',
    },
  },
  drawerShift: {
    transition: `${theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    })} !important`,
    marginLeft: '0 !important',
  },
  listRoot: {
    display: 'flex !important',
    flexDirection: 'column !important',
  },
  listHeader: {
    lineHeight: '1.4rem !important',
    opacity: '0.6 !important',
  },
  listItemRoot: {
    opacity: '1 !important',
    '&:hover': {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  listItemSelected: {
    opacity: '1 !important',
    backgroundColor: 'rgba(0, 0, 0, 0.08) !important',
  },
  listItemLink: {
    width: '100% !important',
    display: 'flex !important',
    alignItems: 'center !important',
    color: 'inherit !important',
    textDecoration: 'none !important',
  },
  listItemIconRoot: {
    fontSize: '.8rem !important',
    minWidth: '35px !important',
  },
  avatarRoot: {
    maxWidth: '24px !important',
    height: '24px !important',
  },
  badgeRoot: {
    backgroundColor: '#FF0000 !important',
  },
  typography: {},
  divider: {
    backgroundColor: `${theme.palette.text.primary} !important`,
    margin: '4px 0 !important',
    opacity: '0.15 !important',
  },
  topNav: {
    marginTop: '48px !important',
  },
  overlay: {
    position: 'fixed !important',
    left: '0 !important',
    top: '0 !important',
    right: '0 !important',
    bottom: '0 !important',
    backgroundColor: '#000 !important',
    opacity: '0.4 !important',
    zIndex: '1000 !important',
  },
});
const StyledLink = styled(RouterLink)(({ theme }) => ({
  width: '100% !important',
  display: 'flex !important',
  alignItems: 'center !important',
  color: 'inherit !important',
  textDecoration: 'none !important',
}));

const StyledAnchor = styled('a')(({ theme }) => ({
  width: '100% !important',
  display: 'flex !important',
  alignItems: 'center !important',
  color: 'inherit !important',
  textDecoration: 'none !important',
}));

const useAuthMenus = (menuItems) => {
  const { conferenceState } = useContext(ConferenceContext);
  const { authState } = useContext(AuthContext);
  const [programRooms, setProgramRooms] = useState([]);

  useEffect(() => {
    if (authState.userLoggedIn) {
      if (conferenceState.conferenceDetail.programDays) {
        const currentDayId = findCurrentDayId(conferenceState.conferenceDetail);
        const currentDay = conferenceState.conferenceDetail.programDays.find(
          (d) => d.id === currentDayId
        );
        setProgramRooms((currentDay && currentDay.programRooms) || []);
      }
    }
  }, [authState.userLoggedIn]);

  if (authState.userLoggedIn) {
    return [
      ...(authState.userLoggedIn &&
      authState.user.access_level === C.VISITOR.ACCESS_LEVEL.MODERATOR
        ? [
            {
              title: 'Moderator',
              icon: 'fact_check',
              url: '/moderator',
            },
          ]
        : []),
      ...menuItems.filter((m) => m.type === 'live' && m.position === 'start'),

      // Loop all program rooms
      ...(window.SETTINGS.HIDE_PROGRAM_ROOMS_IN_MENU
        ? []
        : programRooms.map((room, index) => ({
            title: room.title,
            isLive: room.currentEventId != null,
            avatar: getImage(
              room.bg_picture || `/coeo/room_bg_${index % 5}.jpg`,
              ImageSize.Medium
            ),
            url: `/live/room/${room.id}`,
          }))),
      { divider: true },
      ...menuItems.filter((m) => m.type === 'live' && m.position === 'end'),
    ];
  } else {
    return [];
  }
};

const usePublicMenus = (menuItems) => {
  const location = useLocation();
  const { authState } = useContext(AuthContext);
  const publicTheme = useTheme();

  const smallScreen = useMediaQuery(publicTheme.breakpoints.down('sm'), {
    noSsr: true,
  });

  const siteNavs = menuItems.filter(
    (m) =>
      m.type === 'site' &&
      !(m.onlypublic && authState.userLoggedIn) &&
      m.design !== 'elevated'
  );

  const publicNav =
    siteNavs.length > 0 &&
    (location.pathname.toLowerCase().startsWith('/live') || smallScreen)
      ? [
          ...siteNavs.filter((m) => m.position === 'start'),
          ...siteNavs.filter((m) => m.position === 'end'),
        ]
      : [];

  // IF no left menu (home) and is not on home already
  if (siteNavs.length === 0 && location.pathname !== '/') {
    publicNav.push({
      type: 'site',
      position: 'start',
      title: 'Website',
      url: '/',
      design: '',
    });
  }

  return publicNav;
};

export const LiveContainer = (props) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { conferenceState, welcomeState } = useContext(ConferenceContext);
  const location = useLocation();

  if (welcomeState) {
    return props.children;
  }

  const { authState } = useContext(AuthContext);
  const { menuOpen, setMenuOpen } = useContext(MenuContext);
  const { linkActionCall } = useLinkAction();

  // Simple function to check if menu item is active
  const checkIsActive = useMemo(
    () => (url) => {
      if (!url || url.startsWith('action:')) return false;

      // Use props.isActive if provided
      if (props.isActive) {
        return props.isActive(url);
      }

      // Simple path matching
      const path = location.pathname;
      return url === path || (url !== '/live' && path.startsWith(url + '/'));
    },
    [props.isActive, location.pathname]
  );

  const mainTheme = useTheme();
  const smallScreen = useMediaQuery(mainTheme.breakpoints.down('sm'), {
    noSsr: true,
  });
  const handleDrawerClose = () => setMenuOpen(false);
  const menuItems = conferenceState.conferenceDetail.menu_items;

  const authNav = useAuthMenus(menuItems);
  const publicNav = usePublicMenus(menuItems);

  let menu = [...authNav];

  if (authNav.length > 0 && publicNav.length > 0) {
    // Add divider bwtween live and site navs
    menu.push({ divider: true });
  }

  menu = [...menu, ...publicNav];

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        style={{ marginTop: 0 }}
        sx={{
          ...classes.content,
          ...(menuOpen && menu.length > 0 && classes.contentShift),
          ...(authState.userLoggedIn && classes.topNav),
        }}
      >
        {authState.userLoggedIn && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              zIndex: 999,
            }}
          >
            <VisitorNav {...props} />
          </div>
        )}
        {props.children}
        {menuOpen && smallScreen && menu.length > 0 && (
          <Box onClick={() => setMenuOpen(false)} sx={classes.overlay} />
        )}
      </Box>

      <Drawer
        sx={{
          ...classes.drawer,
          ...(menuOpen && menu.length > 0 && classes.drawerShift),
          '& .MuiDrawer-paper': {
            ...classes.drawerPaper,
          },
        }}
        variant="persistent"
        anchor="left"
        open={menuOpen && menu.length > 0}
      >
        <Box sx={classes.drawerHeader}>
          <IconButton
            size="small"
            onClick={handleDrawerClose}
            sx={classes.drawerHeaderIcon}
          >
            <Icon>chevron_left</Icon>
          </IconButton>
        </Box>
        <Box color="text.main">
          <List sx={classes.listRoot}>
            {menu.map((item, index) =>
              item.header ? (
                <ListSubheader
                  key={`menuitem-${menu.length}-${menu.title}-${index}`}
                  sx={classes.listHeader}
                >
                  {item.header}
                </ListSubheader>
              ) : item.divider ? (
                <Divider
                  sx={classes.divider}
                  key={`menuitem-${menu.length}-${index}`}
                />
              ) : (
                <ListItem
                  key={`menuitem-${menu.length}-${index}`}
                  sx={{
                    ...classes.listItemRoot, // base styles
                    '&.Mui-selected': classes.listItemSelected, // selected state
                  }}
                  // selected={props.isActive ? props.isActive(item.url) : false}
                  selected={checkIsActive(item.url)}
                  className={checkIsActive(item.url) ? 'Mui-selected' : ''}
                >
                  {item.type === 'site' ? (
                    <TopMenuItem
                      data={item}
                      conferenceId={conferenceState.conferenceDetail.id}
                      leftMenu={true}
                    />
                  ) : (
                    (() => {
                      const linkProps = linkActionCall(item);
                      const isExternalUrl =
                        item.url &&
                        (item.url.startsWith('http://') ||
                          item.url.startsWith('https://'));
                      const LinkComponent = isExternalUrl
                        ? StyledAnchor
                        : StyledLink;

                      return (
                        <LinkComponent sx={classes.listItemLink} {...linkProps}>
                          <ListItemIcon sx={classes.listItemIconRoot}>
                            <Badge
                              sx={{
                                '.MuiBadge-dot': classes.badgeRoot,
                              }}
                              invisible={!item.isLive}
                              variant="dot"
                            >
                              {item.avatar ? (
                                <Avatar
                                  src={item.avatar}
                                  sx={classes.avatarRoot}
                                />
                              ) : (
                                <Icon>{item.icon}</Icon>
                              )}
                            </Badge>
                          </ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            sx={{
                              '.MuiListItemText-primary': classes.typography,
                            }}
                          />
                        </LinkComponent>
                      );
                    })()
                  )}
                </ListItem>
              )
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};
